/**
 * lib/auth/auth-client.ts
 * =======================
 * Browser auth client for Amazon Cognito (replaces the better-auth React
 * client). It is a thin wrapper over the server endpoints in
 * routes/api.auth.$.ts — all token exchange happens server-side and tokens live
 * in httpOnly cookies, so this client never sees a JWT.
 *
 * The exported surface intentionally mirrors the previous better-auth client so
 * the ~15 call sites keep working with minimal changes:
 *
 *   authClient.getSession()                         → { data, error }
 *   authClient.useSession()                         → { data, isPending } (reactive)
 *   authClient.signIn.email({ email, password })    → { data, error }   (data.twoFactorRedirect on MFA)
 *   authClient.signIn.social({ provider })          → redirects to Cognito → Google
 *   authClient.signUp.email({ email, password, name })→ { data:{ confirmationRequired }, error }
 *   authClient.signUp.confirm({ email, code, password })→ { data:{ twoFactorRedirect }, error }
 *   authClient.signUp.resendCode({ email })            → { error }
 *   authClient.signOut()                            → { error }
 *   authClient.twoFactor.enable()                   → { data:{ totpURI }, error }
 *   authClient.twoFactor.verifyEnrollment({ code }) → { error }  (enroll confirm)
 *   authClient.twoFactor.verifyTotp({ code })       → { error }  (login challenge)
 *   authClient.twoFactor.disable()                  → { error }
 */

import { useEffect, useState } from 'react';

export interface SessionUser {
  id: string;
  email: string;
  name?: string | null;
  image?: string | null;
  twoFactorEnabled?: boolean;
}
export interface SessionData {
  user: SessionUser;
  session: { token: string; expiresAt: string | null };
}
interface Result<T = unknown> {
  data: T | null;
  error: { message: string; code?: string } | null;
}

async function api<T = unknown>(
  path: string,
  body?: unknown,
  method: 'GET' | 'POST' = 'POST',
): Promise<Result<T>> {
  try {
    const res = await fetch(`/api/auth/${path}`, {
      method,
      credentials: 'same-origin',
      headers: body ? { 'content-type': 'application/json' } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    });
    const raw = await res.text();
    const parsed = raw ? JSON.parse(raw) : {};
    if (!res.ok) {
      return { data: null, error: { message: parsed?.error ?? `Request failed (${res.status})`, code: parsed?.code } };
    }
    return { data: parsed as T, error: null };
  } catch (e) {
    return { data: null, error: { message: e instanceof Error ? e.message : 'Network error' } };
  }
}

// ── Reactive session store ───────────────────────────────────────────────────
let _cache: SessionData | null = null;
let _pending = true;
const _listeners = new Set<() => void>();
function _notify() {
  for (const l of _listeners) l();
}

/** Fetch /api/auth/session and update the reactive store. */
async function fetchSession(): Promise<Result<SessionData>> {
  const res = await api<{
    user: SessionUser | null;
    session: { userId: string; expiresAt: string } | null;
    mfa?: { enrolled: boolean };
  }>('session', undefined, 'GET');

  if (res.error || !res.data?.user) {
    _cache = null;
    _pending = false;
    _notify();
    return { data: null, error: res.error };
  }

  _cache = {
    user: { ...res.data.user, twoFactorEnabled: res.data.mfa?.enrolled ?? false },
    // Access token is httpOnly — not exposed here. token kept as '' for shape
    // compatibility; the edge client fetches it via /api/auth/token instead.
    session: { token: '', expiresAt: res.data.session?.expiresAt ?? null },
  };
  _pending = false;
  _notify();
  return { data: _cache, error: null };
}

// ── Public client ────────────────────────────────────────────────────────────
export const authClient = {
  /** One-shot session fetch; also refreshes the reactive store. */
  async getSession(): Promise<Result<SessionData>> {
    return fetchSession();
  },

  /** Reactive session hook (drop-in for better-auth's useSession). */
  useSession(): { data: SessionData | null; isPending: boolean } {
    const [, force] = useState(0);
    useEffect(() => {
      const cb = () => force((n) => n + 1);
      _listeners.add(cb);
      // Kick off the initial fetch once.
      if (_pending) void fetchSession();
      return () => {
        _listeners.delete(cb);
      };
    }, []);
    return { data: _cache, isPending: _pending };
  },

  signIn: {
    async email({ email, password }: { email: string; password: string }) {
      const res = await api<{ status: 'ok' | 'mfa' }>('sign-in', { email, password });
      if (res.error) return { data: null, error: res.error };
      if (res.data?.status === 'mfa') {
        // Mirror better-auth's onTwoFactorRedirect: send the user to the
        // second-factor screen and signal the caller to stop.
        if (typeof window !== 'undefined') window.location.href = '/auth/mfa-verify';
        return { data: { twoFactorRedirect: true as const }, error: null };
      }
      await fetchSession();
      return { data: { twoFactorRedirect: false as const }, error: null };
    },
    async social({ provider, callbackURL }: { provider: 'google'; callbackURL?: string }) {
      if (typeof window === 'undefined') return;
      const redirect = callbackURL ? `?redirect=${encodeURIComponent(toPath(callbackURL))}` : '';
      window.location.href = `/api/auth/${provider}${redirect}`;
    },
  },

  signUp: {
    /**
     * Start an email+password sign-up. Cognito emails a 6-digit code and the
     * account is NOT signed in yet → resolves `{ confirmationRequired: true }`;
     * the caller then shows the code entry and calls `signUp.confirm`. If the
     * pool auto-confirms, the session is live and `confirmationRequired` is false.
     */
    async email({ email, password, name }: { email: string; password: string; name?: string }) {
      const res = await api<{ status: 'ok' | 'confirm_required' }>('sign-up', { email, password, name });
      if (res.error) return { data: null, error: res.error };
      const confirmationRequired = res.data?.status === 'confirm_required';
      if (!confirmationRequired) await fetchSession();
      return { data: { confirmationRequired }, error: null };
    },
    /**
     * Confirm a pending sign-up with the emailed code, then sign in (the
     * password is re-sent from form state). Resolves `{ twoFactorRedirect }`;
     * on success the reactive session store is refreshed.
     */
    async confirm({ email, code, password }: { email: string; code: string; password: string }) {
      const res = await api<{ status: 'ok' | 'mfa' }>('confirm-sign-up', { email, code, password });
      if (res.error) return { data: null, error: res.error };
      if (res.data?.status === 'mfa') {
        if (typeof window !== 'undefined') window.location.href = '/auth/mfa-verify';
        return { data: { twoFactorRedirect: true as const }, error: null };
      }
      await fetchSession();
      return { data: { twoFactorRedirect: false as const }, error: null };
    },
    /** Re-send the sign-up verification code. */
    async resendCode({ email }: { email: string }) {
      const res = await api('resend-code', { email });
      return { error: res.error };
    },
  },

  async signOut() {
    const res = await api('sign-out');
    _cache = null;
    _pending = false;
    _notify();
    return { error: res.error };
  },

  twoFactor: {
    /** Begin TOTP enrollment — returns the otpauth URI (as totpURI for the QR). */
    async enable(_opts?: { password?: string; issuer?: string }) {
      const res = await api<{ secret: string; otpauthUri: string }>('mfa/enable');
      if (res.error) return { data: null, error: res.error };
      return { data: { totpURI: res.data!.otpauthUri, secret: res.data!.secret }, error: null };
    },
    /** Confirm the first TOTP code during enrollment. */
    async verifyEnrollment({ code }: { code: string }) {
      const res = await api('mfa/verify', { code });
      if (!res.error) await fetchSession();
      return { error: res.error };
    },
    /** Complete the login-time TOTP challenge. */
    async verifyTotp({ code }: { code: string; trustDevice?: boolean }) {
      const res = await api<{ status: 'ok' }>('mfa/challenge', { code });
      if (!res.error) await fetchSession();
      return { error: res.error };
    },
    async disable(_opts?: { password?: string }) {
      const res = await api('mfa/disable');
      if (!res.error) await fetchSession();
      return { error: res.error };
    },
  },

  /** Request a password-reset code (Cognito ForgotPassword). */
  async forgotPassword({ email }: { email: string }) {
    const res = await api('forgot-password', { email });
    return { error: res.error };
  },
  async resetPassword({ email, code, password }: { email: string; code: string; password: string }) {
    const res = await api('reset-password', { email, code, password });
    return { error: res.error };
  },
};

/** Reduce an absolute callback URL to a same-site path for the redirect param. */
function toPath(urlOrPath: string): string {
  try {
    if (urlOrPath.startsWith('/')) return urlOrPath;
    const u = new URL(urlOrPath);
    return u.pathname + u.search;
  } catch {
    return '/post-login';
  }
}

export type AuthClient = typeof authClient;
